import { DragEndEvent, DragOverEvent, DragStartEvent } from '@dnd-kit/core';
import { isSameDay } from 'date-fns';
import { useCallback, useEffect, useState } from 'react';
import { ID } from 'shared/types/id';
import { TaskCardDateSection } from 'shared/types/orderable-section';
import { TaskCardTask } from 'shared/types/task-card-task';
import { parseIdDateKey } from 'shared/utils/format-id-date-key';
import {
  handleItemBetweenSectionsMovement,
  handleItemWithinSectionMovement,
} from 'shared/utils/handle-section-item-movement';

export const useDroppableDatesControls = ({
  initialSections,
  onDragEnd,
  taskSectionExclusionMap = {},
}: {
  initialSections: TaskCardDateSection[];
  onDragEnd?: (
    sections: TaskCardDateSection[],
    movedTask: TaskCardTask,
  ) => void;
  taskSectionExclusionMap?: Record<ID, ID[]>;
}) => {
  const [currentSections, setCurrentSections] = useState(initialSections);
  const [activeTaskId, setActiveTaskId] = useState<ID>();
  const [originalSectionId, setOriginalSectionId] = useState<ID>();

  useEffect(() => {
    setCurrentSections(initialSections);
  }, [initialSections]);

  const handleDragStart = useCallback(
    ({ active }: DragStartEvent) => {
      const id = active.id.toString();
      setActiveTaskId(id);

      const sectionDate = parseIdDateKey(id).date;
      if (!sectionDate) {
        return;
      }

      const originalSection = currentSections.find(({ deadline }) =>
        isSameDay(deadline, sectionDate),
      );

      setOriginalSectionId(originalSection?.id);
    },
    [currentSections],
  );

  const handleDragOver = useCallback(
    ({ active, over }: DragOverEvent) => {
      if (!over?.id || over.id === active.id || !activeTaskId) {
        return;
      }

      setCurrentSections((oldSections) => {
        const excludedSectionIds = taskSectionExclusionMap[activeTaskId] || [];

        const { sections } = handleItemBetweenSectionsMovement(
          oldSections,
          active,
          over,
          excludedSectionIds.filter((id) => id !== originalSectionId),
        );

        return sections;
      });
    },
    [activeTaskId, originalSectionId, taskSectionExclusionMap],
  );

  const handleDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      setActiveTaskId(undefined);
      if (!over?.id || !activeTaskId) {
        return;
      }

      const excludedSectionIds = taskSectionExclusionMap[activeTaskId] || [];

      const { sections, activeItem: activeTask } =
        handleItemWithinSectionMovement<TaskCardTask, TaskCardDateSection>(
          currentSections,
          active,
          over,
          excludedSectionIds.filter((id) => id !== originalSectionId),
        );

      if (!activeTask) {
        return;
      }

      return onDragEnd?.(sections, activeTask);
    },
    [
      activeTaskId,
      currentSections,
      onDragEnd,
      originalSectionId,
      taskSectionExclusionMap,
    ],
  );

  const handleDragCancel = useCallback(() => {
    setCurrentSections(initialSections);
    setActiveTaskId(undefined);
  }, [initialSections]);

  return {
    sections: currentSections,
    activeId: activeTaskId,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    handleDragCancel,
    setSections: setCurrentSections,
  };
};
